<template>
  <div class="p-6 dark:text-gray-50"
       v-if="canAdd || user?.is_staff" 
    >
    <div class="flex mb-5 space-x-3">
      <h1 class="text-2xl font-semibold dark:text-white">Add New Division</h1>
      <div class="relative group">
      <button
        type="button"
        class="hover:bg-transparent bg-transparent"
        @click="startTutorial"
      >
      <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-circle-question-mark dark:text-gray-50 text-gray-900"><circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/><path d="M12 17h.01"/></svg>
      </button>
        <span class="absolute top-full text-xs right-1/2 -translate-y-1/2 mb-1 px-2 py-0.5 glass-card opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap">Tutorial</span>
      </div>
    </div>
  


    <form @submit.prevent="submitForm" class="space-y-6 p-6 glass-card">
      <div
        v-for="field in divisionFields"
        :key="field.key"
        class="flex flex-col"
      >
        <label :for="field.key" class="mb-1 font-medium text-gray-700 dark:text-white">{{
          field.label
        }}</label>
        <input
          v-if="field.type !== 'select'"
          :id="field.key"
          v-model="divisionData[field.key]"
          :type="field.type"
          @focus="clearFieldError(field.key)"
          :placeholder="field.placeholder"
          :class="[ 'border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2', fieldErrors[field.key]
                ? 'ring-2 ring-red-500  focus:ring-red-500'
                : 'focus:ring-blue-500'
          ]"
        />
        <GlassSelect
          v-else
          :id="field.key"
          v-model="divisionData[field.key]"
          @focus="clearFieldError(field.key)"
          :options="tenant.organizations.value"
          :searchable="true"
          placeholder="Select an organisation"
          :error="!!fieldErrors[field.key]"
        />
        <FieldError :message="fieldErrors[field.key]"/>
      </div>
      <div class="flex justify-end space-x-2">
        <button
          type="button"
          @click="$router.push({ name: 'Divisions', query: this.tenant.addSchemaParam() })"
          class="btn bg-gray-300 text-gray-700 hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-500"
        >
          Cancel
        </button>
        <button
          id="submit-division-btn"
          type="submit"
          class="btn bg-blue-600 text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          Add Division
        </button>
      </div>
    </form>
  </div>

  <!-- Error Message -->
  <PageError v-if="permsLoaded && !canAdd && !user?.is_staff"
    :error="errorMessages['403']"
    :errorCode="'403'" 
  />
</template>

<script>
import { createDivision } from "@/services/divisionService";
import { getAllOrgs } from "@/services/orgService";
import GlassSelect from "@/components/GlassSelect.vue";
import FieldError from "@/components/FieldError.vue";
import { useStore } from "@/store";
import PageError from "@/components/PageError.vue";
import { errorMessages } from "@/utils/errorMessages";
import { useMultiTenant } from "@/composables/useMultiTenant";
import { useTutorial } from "@/composables/tutorial";

export default {
  components: {
    FieldError,
    GlassSelect,
    PageError,
  },
  setup() {
    const tenant = useMultiTenant();
    const { start } = useTutorial();
    return { tenant, _start: start };
  },
  data() {
    return {
      errorMessages: errorMessages,
      fieldErrors: {},
      divisionData: {
        name: "",
        abbreviated_name: "",
        code: "",
        org: null,
      },
      canAdd: false,
      divisionFields: [
        { key: "name", label: "Name", type: "text", required: true, placeholder: "Division" },
        {
          key: "abbreviated_name",
          label: "Abbreviated Name",
          type: "text",
          required: true,
          placeholder: "DV"
        },
        { key: "code", label: "Code", type: "text", required: true, placeholder: "DV001" },
        { key: "org", label: "Organisation", type: "select", required: true },
      ],
      addDivisionTutorialSteps: [
        {
          element: '#name',
          action: 'type',
          value: 'Division',
          message: 'Enter the full name of the division.'
        },
        {
          element: '#abbreviated_name',
          action: 'type',
          value: 'DV',
          message: 'Enter a short abbreviated name for the division.'
        },
        {
          element: '#code',
          action: 'type',
          value: 'DV001',
          message: 'Enter a unique code for this division (e.g. DV001).'
        },
        {
          element: '#org',
          action: 'select',
          value: -1,
          message: 'Select the organisation this division belongs to.'
        },
        {
          element: '#submit-division-btn',
          action: 'display',
          message: 'Click Add Division to submit the form.'
        },
      ],
    };
  },
  async mounted() {
    await this.tenant.fetchOrganizations();
  },
  computed: {
    user(){
      return useStore.getUser();
    },
    permsLoaded() {
      return useStore.permissionsLoadedRef.value;
    },
  },
  watch: {
    user: {
      handler(user) {
        if (user) {
          this.checkPermissions();
        }
      },
      immediate: true
    },
    'divisionData.org'(orgId) {
      if (!orgId) return;
      const selectedOrg = this.tenant.organizations.value.find(org => org.id === orgId);
      if (!selectedOrg?.schema_name) return;
      
      const { schemaName, ...existingQuery } = this.$route.query;
      this.$router.replace({
        query: {
          ...existingQuery,
          client: selectedOrg.schema_name
        }
      });
    }
  },
  methods: {
    async startTutorial() {
      const originalOrgs = [...this.tenant.organizations.value];
      const originalData = { ...this.divisionData };

      this.tenant.organizations.value = [{ id: -1, name: 'Tutorial Organisation' }];

      await this._start(this.addDivisionTutorialSteps, () => {
        this.tenant.organizations.value = originalOrgs;
        this.divisionData = { ...originalData };
      });
    },
    clearFieldError(fieldName){
      if (this.fieldErrors[fieldName]) {
        delete this.fieldErrors[fieldName];
        delete this.fieldErrors['non_field_errors'];
      }
    },
    validateFields(fields, fieldsToCheck) {
      const errors = {};
      for (const fieldName of fieldsToCheck) {
        const value = fields[fieldName];
        if (!value || (typeof value === "string" && !value.trim()) || (value instanceof Array && value.length === 0)) {
          errors[fieldName] = "This field cannot be empty";
        }
      }
      return errors;
    },
    async checkPermissions() {
      if (this.user) {
        this.canAdd = await useStore.hasPermission("Can add division");
      }
    },
    async submitForm() {
      try {

        this.fieldErrors = this.validateFields(this.divisionData, ['name', 'org', 'code', 'abbreviated_name'])
        if (Object.keys(this.fieldErrors).length > 0) return;

        const validation = this.tenant.validateSchemaForOperation();
        if (!validation.valid) {
          this.error = "Malformed request. Failed to create division!";
          this.errorCode = "422!";
          return;
        }

        this.fieldErrors = this.validateFields(this.divisionData, ['name', 'org', 'code', 'abbreviated_name'])
        if (Object.keys(this.fieldErrors).length > 0) return;

        const selectedOrg = this.tenant.organizations.value.find(
          org => org.id === this.divisionData.org
        );
        const schemaName = selectedOrg.schema_name;
        const newDivision = await createDivision(this.divisionData, schemaName);
        await this.tenant.switchOrganization(selectedOrg.id, () => {});

        // Redirect to the division list or the newly created division's details
        this.$router.push({ 
          name: "Divisions", 
        });
      } catch (error) {

        this.fieldErrors = {}; 
        const items = this.divisionFields;
        const errData = error?.response?.data;

        items.forEach(element => {
          const fieldError = errData?.[element.key];
          if (fieldError?.[0]) {
            this.fieldErrors[element.key] = fieldError[0]; 
          }
        });

        console.error("Error creating division:", error);
        // Handle error (e.g., show error message to user)
      }
    },
  },
};
</script>
